import { createAsyncThunk } from "@reduxjs/toolkit";
import API_BASE_URL from "../config";
import axios from "axios";
import { auth } from "../firebase";

// Configure global axios defaults
axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Async interceptor: always use a fresh Firebase token (auto-refresh)
axios.interceptors.request.use(
    async (config) => {
        try {
            if (auth.currentUser) {
                // Force-refresh=false: returns cached token if still valid, refreshes if expired
                const freshToken = await auth.currentUser.getIdToken(false);
                localStorage.setItem('firebase_token', freshToken);
                config.headers.Authorization = `Bearer ${freshToken}`;
            } else {
                // Fallback: use stored token (e.g. page reload before Firebase re-hydrates)
                const token = localStorage.getItem('firebase_token');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
            }
        } catch (e) {
            // If token refresh fails, try the stored token as last resort
            const token = localStorage.getItem('firebase_token');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

const getDataClimats = createAsyncThunk("jamaslens/getDataClimats", () => {
    return axios
        .get(`${API_BASE_URL}/products`)
        .then(pfe => pfe.data);
});

const getDataProjets = createAsyncThunk("jamaslens/getDataProjets", () => {
    return axios
        .get(`${API_BASE_URL}/listP`)
        .then(pfe => pfe.data);
});

const getDataComptes = createAsyncThunk("jamaslens/getDataComptes", () => {
    return axios
        .get(`${API_BASE_URL}/comptes`)
        .then(pfe => pfe.data);
});

const getDataCommandes = createAsyncThunk("jamaslens/getDataCommandes", () => {
    return axios
        .get(`${API_BASE_URL}/commandes`)
        .then(pfe => pfe.data);
});

const getPanier = createAsyncThunk("jamaslens/getPanier", () => {
    return axios
        .get(`${API_BASE_URL}/cart`)
        .then(pfe => pfe.data);
});

const addToPanier = createAsyncThunk("jamaslens/addToPanier", (obj) => {
    return axios
        .post(`${API_BASE_URL}/cart`, obj)
        .then(pfe => pfe.data);
});

const updateCartItem = createAsyncThunk("jamaslens/updateCartItem", (obj) => {
    return axios
        .put(`${API_BASE_URL}/cart/${obj.id}`, obj)
        .then(pfe => pfe.data);
});

const deleteCartItem = createAsyncThunk("jamaslens/deleteCartItem", (id) => {
    return axios
        .delete(`${API_BASE_URL}/cart/${id}`)
        .then(pfe => pfe.data);
});


const addClimatisseur = createAsyncThunk("jamaslens/addClimatisseur", async (obj, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/products`, obj);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: error.message });
    }
});

const updateClimatisseur = createAsyncThunk("jamaslens/updateClimatisseur", async (obj, { rejectWithValue }) => {
    try {
        const response = await axios.put(`${API_BASE_URL}/products/${obj.id}`, obj);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: error.message });
    }
});

const deleteClimatisseur = createAsyncThunk("jamaslens/deleteClimatisseur", (id, { rejectWithValue }) => {
    return axios
        .delete(`${API_BASE_URL}/products/${id}`)
        .then(pfe => pfe.data)
        .catch(error => {
            if (error.response && error.response.status === 404) return { message: "Already deleted", id };
            return rejectWithValue(error.response?.data || { message: error.message });
        });
});

const getClimatisseurInfo = createAsyncThunk("jamaslens/getClimatisseurInfo", (id) => {
    return axios
        .get(`${API_BASE_URL}/products/${id}`)
        .then(pfe => pfe.data);
});



const addProjet = createAsyncThunk("jamaslens/addProjet", async (obj, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/listP`, obj);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: error.message });
    }
});

const updateProjet = createAsyncThunk("jamaslens/updateProjet", async (obj, { rejectWithValue }) => {
    try {
        const response = await axios.put(`${API_BASE_URL}/listP/${obj.id}`, obj);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: error.message });
    }
});

const deleteProjet = createAsyncThunk("jamaslens/deleteProjet", (id, { rejectWithValue }) => {
    return axios
        .delete(`${API_BASE_URL}/listP/${id}`)
        .then(pfe => pfe.data)
        .catch(error => {
            if (error.response && error.response.status === 404) return { message: "Already deleted", id };
            return rejectWithValue(error.response?.data || { message: error.message });
        });
});

const getProjetInfo = createAsyncThunk("jamaslens/getProjetInfo", (id) => {
    return axios
        .get(`${API_BASE_URL}/listP/${id}`)
        .then(pfe => pfe.data);
});

const addCompte = createAsyncThunk("jamaslens/addCompte", async (obj, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/comptes`, obj);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: error.message });
    }
});

const updateCompte = createAsyncThunk("jamaslens/updateCompte", async (obj, { rejectWithValue }) => {
    try {
        const response = await axios.put(`${API_BASE_URL}/comptes/${obj.id}`, obj);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: error.message });
    }
});

const deleteCompte = createAsyncThunk("jamaslens/deleteCompte", (id, { rejectWithValue }) => {
    return axios
        .delete(`${API_BASE_URL}/comptes/${id}`)
        .then(pfe => pfe.data)
        .catch(error => {
            if (error.response && error.response.status === 404) return { message: "Already deleted", id };
            return rejectWithValue(error.response?.data || { message: error.message });
        });
});

const addCommande = createAsyncThunk("jamaslens/addCommande", (obj) => {
    return axios
        .post(`${API_BASE_URL}/commandes`, obj)
        .then(pfe => pfe.data);
});

const updateCommande = createAsyncThunk("jamaslens/updateCommande", (obj) => {
    return axios
        .put(`${API_BASE_URL}/commandes/${obj.id}`, obj)
        .then(pfe => pfe.data);
});

const deleteCommande = createAsyncThunk("jamaslens/deleteCommande", (id, { rejectWithValue }) => {
    return axios
        .delete(`${API_BASE_URL}/commandes/${id}`)
        .then(pfe => pfe.data)
        .catch(error => {
            if (error.response && error.response.status === 404) return { message: "Already deleted", id };
            return rejectWithValue(error.response?.data || { message: error.message });
        });
});

const patchCompte = createAsyncThunk("jamaslens/patchCompte", async (obj, { rejectWithValue }) => {
    try {
        const response = await axios.patch(`${API_BASE_URL}/comptes/${obj.id}`, obj);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: error.message });
    }
});

const login = createAsyncThunk("jamaslens/login", async (credentials, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/login`, credentials);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: error.message || "Erreur de connexion" });
    }
});

const register = createAsyncThunk("jamaslens/register", async (userData, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/register`, userData);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: error.message || "Erreur lors de l'inscription" });
    }
});

const getDataHistories = createAsyncThunk("jamaslens/getDataHistories", () => {
    return axios
        .get(`${API_BASE_URL}/histories`)
        .then(res => res.data);
});

const deleteHistory = createAsyncThunk("jamaslens/deleteHistory", (id) => {
    if (id === 'all') {
        return axios
            .delete(`${API_BASE_URL}/histories-clear`)
            .then(res => res.data);
    }
    return axios
        .delete(`${API_BASE_URL}/histories/${id}`)
        .then(res => res.data);
});

const addHistory = createAsyncThunk("jamaslens/addHistory", (obj) => {
    return axios
        .post(`${API_BASE_URL}/histories`, obj)
        .then(res => res.data);
});

const searchProducts = createAsyncThunk("jamaslens/searchProducts", (query) => {
    return axios
        .get(`${API_BASE_URL}/products/search?q=${query}`)
        .then(pfe => pfe.data);
});

const uploadUserImage = createAsyncThunk("jamaslens/uploadUserImage", async ({ id, formData }) => {
    return axios
        .post(`${API_BASE_URL}/comptes/${id}/image`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
        .then(pfe => pfe.data);
});
const toggleCommentInteraction = createAsyncThunk("jamaslens/toggleCommentInteraction", async ({ id, userEmail, type }) => {
    return axios
        .post(`${API_BASE_URL}/comments/${id}/toggle-interaction`, { userEmail, type })
        .then(res => res.data);
});

const firebaseSync = createAsyncThunk("jamaslens/firebaseSync", async (payload, { rejectWithValue }) => {
    try {
        const data = typeof payload === 'string' ? { token: payload } : payload;
        const response = await axios.post(`${API_BASE_URL}/firebase/sync`, data);
        if (response.data.require_code) {
            return rejectWithValue(response.data);
        }
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: error.message || "Erreur de synchronisation Firebase" });
    }
});

const sendAuthCode = createAsyncThunk("jamaslens/sendAuthCode", async (email, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/firebase/send-code`, { email });
        return response.data;
    } catch (err) {
        if (err.response && err.response.data && err.response.data.message) {
            return rejectWithValue(new Error(err.response.data.message));
        }
        return rejectWithValue(err);
    }
});

const verifyAuthCode = createAsyncThunk("jamaslens/verifyAuthCode", async (data, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/firebase/verify-code`, data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: "Code invalide أو expiré" });
    }
});

const verifyRegister = createAsyncThunk("jamaslens/verifyRegister", async (data, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/firebase/verify-register`, data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || { message: error.response?.data?.message || "Erreur lors de la création du compte" });
    }
});

const addComment = createAsyncThunk("jamaslens/addComment", (obj) => {
    return axios
        .post(`${API_BASE_URL}/comments`, obj)
        .then(res => res.data);
});

const deleteComment = createAsyncThunk("jamaslens/deleteComment", (id, { rejectWithValue }) => {
    return axios
        .delete(`${API_BASE_URL}/comments/${id}`)
        .then(res => res.data)
        .catch(error => {
            if (error.response && error.response.status === 404) return { message: "Already deleted", id };
            return rejectWithValue(error.response?.data || { message: error.message });
        });
});

const updateComment = createAsyncThunk("jamaslens/updateComment", (obj) => {
    return axios
        .put(`${API_BASE_URL}/comments/${obj.id}`, obj)
        .then(res => res.data);
});

const getContactSettings = createAsyncThunk("jamaslens/getContactSettings", () => {
    return axios
        .get(`${API_BASE_URL}/contact-settings`)
        .then(res => res.data);
});

const updateContactSettings = createAsyncThunk("jamaslens/updateContactSettings", (data, { rejectWithValue }) => {
    return axios
        .put(`${API_BASE_URL}/contact-settings`, data)
        .then(res => res.data)
        .catch(error => rejectWithValue(error.response?.data || { message: error.message }));
});

export {
    getDataClimats, getDataProjets, getDataComptes, getDataCommandes, getPanier, addToPanier, deleteCartItem, updateCartItem,
    addClimatisseur, updateClimatisseur, deleteClimatisseur, getClimatisseurInfo,
    addProjet, updateProjet, deleteProjet, getProjetInfo,
    addCompte, updateCompte, patchCompte, deleteCompte,
    addCommande, updateCommande, deleteCommande,
    login, register, searchProducts, uploadUserImage, toggleCommentInteraction, addComment, deleteComment, updateComment, getDataHistories, deleteHistory, addHistory, firebaseSync, sendAuthCode, verifyAuthCode, verifyRegister,
    getContactSettings, updateContactSettings
};